/**
 * The pipeline strip — `[audience mark] audience → [brand mark] destination audience`. It is the UX
 * IDENTIFIER for an activation, and this file is the only place it is drawn.
 *
 * An activation has no name of its own. The platform addresses it by a uuid, and the destination
 * Lookout mints for it is a `{accountName}-{uuid}` handle nobody typed and nobody can rename
 * (`isMintedDestinationName`). What actually tells two activations apart is the PAIR: two syncs out of
 * the same audience differ only in their far side, two syncs into the same far side differ only in
 * their near side. So the pair *is* the identity, and a surface that has to say "which activation is
 * this" draws the whole relation rather than half of it — which is why the list row, the detail
 * header, the run instrument and the Activate wizard now all read the same.
 *
 * Five rules live here rather than in each caller:
 *
 * 1. **`Users` is the audience glyph.** Not a warehouse one — commit b4e840ae deliberately replaced
 *    `Database` on this exact strip, because what is being shipped is a membership, and `Users` is
 *    already what the nav (`AppSidebar`) uses for the same subject. Do not reintroduce a warehouse mark.
 *    Superseded by ACT2-852: the near-side mark is the audience's warehouse BRAND where that type has
 *    a registered asset, and `Users` for missing, empty or unregistered types. The generic `Database`
 *    glyph stays banned — #645's point holds for a generic icon, while a brand mark states where the
 *    audience is queried from, as #761 does on the Audiences list.
 * 2. **The far-side mark resolves through `destinationTypeIcon`**, which falls back for a definition
 *    name we ship no asset for — so the slot is never blank, which would read as a broken render.
 * 3. **An unresolved far side is typeset as an identifier** (`destinationIsIdentifier`). Rungs two and
 *    three of the ladder in `lib/activation-list.ts` are an id and an account, not the audience's own
 *    name; set in the same weight as a name they claim the product named something `23847…`.
 * 4. **Neither label is invented here.** Callers pass the resolvers' output (`audienceLabel`,
 *    `destinationAudienceLabel`); this component never substitutes a placeholder for a missing one.
 * 5. **The strip carries the sync's on/off state** (`enabled` → {@link ActivationStatusDot}), which is
 *    why the list has no Status column any more. An ABSENT `enabled` draws no dot at all, and that is a
 *    first-class case rather than a default — see the prop's own comment.
 *
 * Two constraints shape the markup:
 *
 * - **All-phrasing content — `<span>`s only, no `<div>`.** The activations table renders its identity
 *   cell inside a row-activator `<button>` (`activatesRow`), which permits no flow content. The same
 *   cell also permits no second focusable element, hence `tooltips={false}`: `TruncateWithTooltip`
 *   plants a Radix trigger on its span, and a trigger inside the activator is a second tab stop.
 *   `audienceHref` is the second prop cut to that same shape and for the same reason — an `<a>` is
 *   phrasing content and so passes the first half of this rule, but it is interactive and so fails the
 *   second. Both default to the safe value, so a new caller inside an activator is correct by default
 *   and only an explicit opt-in can break it.
 * - **The arrow is not the only thing conveying direction.** Every glyph is decorative and hidden, so
 *   an unaided reading of the strip would be two names with nothing between them. The roles and the
 *   direction are carried by `sr-only` text instead: "Audience X activates into Destination audience Y".
 *
 * Lives in `activations/` and not `_shared/`: it renders a domain concept, and `_shared/` is for the
 * shell and the design-system primitives, not a staging area. The Activate wizard
 * (`audiences/components/AudienceActivationSheet`) imports it across the domain boundary — the mirror
 * of the import every activations surface already makes for `@/audiences/icons/destinationTypes`.
 */
import { ArrowRight, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { cn } from '@/_shared/lib/utils';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';
import { isKnownWarehouseType, warehouseTypeMeta } from '@/settings/components/warehouseAccount/warehouseTypes';

import { warehouseTypeLabel } from '../lib/activation-list';

/**
 * Three sizes, because the strip is drawn at three altitudes and a free-form `className` would let
 * them drift: `sm` is the muted one-liner under a heading (the wizard sheet), `md` is the one that
 * titles a COMPONENT (a table row, a panel of the run instrument), and `lg` is the one that titles a
 * PAGE — the activation detail header, where the strip IS the `<h1>` rather than a subtitle beneath
 * one.
 *
 * `lg`'s label is the page-title recipe verbatim (`text-lg font-semibold tracking-tight`), which is
 * what the `<h1>` it replaced already carried — so promoting the strip reuses a heading style rather
 * than inventing one. `md` would not do: at `text-sm` a page title renders smaller than the
 * `<h2>Last run</h2>` in the card directly beneath it.
 */
type StripSize = 'sm' | 'md' | 'lg';

/**
 * How the strip divides its width — the only thing that differs between a stacked list and a lone
 * heading, and the reason this is one component rather than two.
 *
 * `flow` is natural flex: the near side yields, the far side is capped, and the arrow lands wherever
 * the audience name happens to end. Right for a strip with nothing above or below it to line up with.
 *
 * `tracks` is a fixed three-column template, for a strip rendered once per row down a list. Measured
 * over six real rows of the live workspace, `flow` scatters the arrow across **193.9px** — a
 * four-character audience name pulls it hard left while a forty-one-character one shoves it against the
 * far side's cap — so it reads as a scatter rather than a column. The same six rows under `tracks`
 * measure **0.0px** of drift, because the arrow's x is `near-track width + gap` and both are a constant
 * fraction of a container every row shares. No measured widths, no `ResizeObserver`, no pixels.
 */
type StripLayout = 'flow' | 'tracks';

/** Icon + inter-item gap + the strip's own (muted) text color, which the glyphs inherit. */
const FRAME_CLASSES: Record<StripSize, string> = {
  sm: 'gap-1.5 text-xs text-muted-foreground',
  md: 'gap-2 text-sm text-muted-foreground',
  lg: 'gap-2 text-lg text-muted-foreground',
};

/**
 * The root's sizing strategy, and the whole of what `layout` swaps.
 *
 * **`minmax(0, Nfr)`, never a bare `Nfr`.** A bare `3fr` is `minmax(auto, 3fr)`, whose `auto` minimum
 * is the item's MIN-CONTENT width — so one long unbroken audience name would push its own track wider
 * and move the arrow again, which is the exact bug this template exists to fix. With a `0` minimum the
 * track takes exactly its share and the label truncates inside it, which is what `truncate` already
 * expects. Changing these to bare `fr` reverts the feature while still looking correct on short names.
 *
 * **3fr / 2fr is benched, not eyeballed.** A fixed share is a RESERVATION, not a request: under
 * `tracks` the far side takes its share whether it needs it or not, so the only question is the
 * smallest far-side reservation that still fits every real far-side label. `8fr/5fr`, `5fr/3fr` and
 * `2fr/1fr` were all measured at the real strip width and all three cut a label (the two that bind are
 * the unresolved mono identifier `120243961897900490` and `sql-audience sink pg`). 3fr/2fr is the
 * cheapest split that keeps the discriminator whole. Do not re-open it by eye.
 *
 * The `sr-only` connective between the arrow and the far side is `position: absolute`, so it is not a
 * grid item and does not claim a fourth track — the same reason it costs no gap in `flow`.
 */
const LAYOUT_CLASSES: Record<StripLayout, string> = {
  flow: 'flex min-w-0 items-center',
  tracks: 'grid min-w-0 items-center grid-cols-[minmax(0,3fr)_auto_minmax(0,2fr)]',
};

/**
 * `max-w-3/5` and `shrink-0` are both FLOW concepts and are dropped under `tracks`: the template is the
 * cap there, and `shrink-0` is meaningless on a grid item. Keeping them would double-cap the far side.
 */
const FAR_SIDE_CLASSES: Record<StripLayout, string | undefined> = {
  flow: 'max-w-3/5 shrink-0',
  tracks: undefined,
};

const MARK_CLASSES: Record<StripSize, string> = {
  sm: 'size-3 shrink-0',
  md: 'size-4 shrink-0',
  lg: 'size-5 shrink-0',
};

const LABEL_CLASSES: Record<StripSize, string> = {
  sm: 'text-xs text-foreground',
  md: 'text-sm font-medium text-foreground',
  lg: 'text-lg font-semibold tracking-tight text-foreground',
};

/**
 * The same KIND of string at every altitude — mono, muted, normal weight — because an identifier is
 * not a smaller heading, it is a different kind of string, and it says so identically wherever it
 * appears. Only the STEP scales.
 *
 * `font-normal` undoes `md`'s medium weight. `lg` additionally steps down one size and undoes its
 * `tracking-tight`, which is exactly what the `<h1>` it replaced already did for an unresolved far
 * side (`font-mono text-sm text-muted-foreground`) — a `text-xs` identifier under a `size-5` mark
 * reads as a rendering failure rather than as a deliberate demotion.
 */
const IDENTIFIER_LABEL_CLASSES: Record<StripSize, string> = {
  sm: 'font-mono text-xs font-normal text-muted-foreground',
  md: 'font-mono text-xs font-normal text-muted-foreground',
  lg: 'font-mono text-sm font-normal tracking-normal text-muted-foreground',
};

/** The screen-reader role word for each side — see "the arrow is not the only thing" above. */
const ENDPOINT_ROLE: Record<'audience' | 'destination', string> = {
  audience: 'Audience ',
  destination: 'Destination audience ',
};

/**
 * The connective, which is also where a disabled sync tells the truth an arrow cannot.
 *
 * The structural objection to putting the mark at the HEAD of the strip is real and unanswered:
 * enabled/disabled is a property of the pipe, and the head is the near endpoint. This is where that
 * gets said correctly, and it costs nothing — a disabled sync is not activating anything into anywhere,
 * so the tense of the sentence carries the state without a second announcement.
 *
 * A strip with no `enabled` at all (the wizard) keeps the present tense: it is describing the sync the
 * reader is about to create, not asserting one that already runs.
 */
const CONNECTIVE: Record<'enabled' | 'disabled', string> = {
  enabled: ' activates into ',
  disabled: ' is not currently syncing into ',
};

/**
 * FILLED for enabled, HOLLOW for disabled — and the fill, not the hue, is what carries the state.
 *
 * `success`/`danger` are the same two token families `Badge`'s `success`/`error` variants are cut from,
 * so this cannot drift from the `Disabled` badge it replaced. Green is a deliberate, accepted collision
 * with `SyncStatusBadge`'s `Completed`, two columns right: a green dot means "the switch is on", a green
 * badge means "the last run succeeded". They are four columns and two type sizes apart, the dot carries
 * no text, and the alternative — a neutral `bg-muted-foreground` dot — reads as "unknown" in a product
 * that already spends grey on things it has no answer for (`--info`, the identifier styling, the em dash
 * in Schedule).
 *
 * The disc/ring difference is the NON-COLOUR carrier, and it is load-bearing rather than decorative: a
 * reader who cannot separate those two hues, a greyscale display or a monochrome print still sees solid
 * against outline, which is what WCAG 1.4.1 actually asks for. Stated honestly: at `size-1.5` that is a
 * weak shape distinction met alone and a clear one met in a stacked list, which is the surface it was
 * designed for. Both hues clear 1.4.11 on contrast in both themes; contrast is not shape, hence this.
 *
 * The geometry lives in {@link STATUS_DOT_SIZE}. The fill/ring pair here is size-INDEPENDENT and must
 * stay that way — see that constant for why the stroke deliberately does not scale with the diameter.
 */
const STATUS_DOT_CLASSES: Record<'enabled' | 'disabled', string> = {
  enabled: 'bg-success',
  disabled: 'border border-danger bg-transparent',
};

/**
 * The dot's diameter per altitude: the same instrument at every altitude, which means it scales WITH
 * the altitude rather than being frozen against it. What must not change is its KIND — an identifier
 * is never a smaller heading, a state light is never a smaller badge.
 *
 * It scales at all because `lg` is the "met alone" case this dot is weakest in. The comment above
 * concedes that at `size-1.5` the disc/ring difference is "a weak shape distinction met alone and a
 * clear one met in a stacked list" — and the detail header is met-alone by definition: one strip, no
 * column of peers to compare against. Every other mark in the strip already steps with the size
 * (`MARK_CLASSES`: 3 → 4 → 5), so a frozen dot falls from 50% of the mark at `sm` to 30% at `lg`.
 *
 * **The stroke stays 1px while the diameter grows, and that is a decision, not an oversight.** The
 * ring fails when it reads as a lighter DISC, which is a function of the interior fraction rather
 * than of the stroke in isolation. At `size-1.5`, a 6px dot with a 1px border leaves a 4px hole — 44%
 * of the area. At `size-2`, an 8px dot with the same 1px border leaves 6px: **56%**, i.e. MORE hollow
 * than the reference, with the stroke still 12.5% of the diameter. Thickening to `border-2` at 8px
 * would close the hole back to 4px (25%) — *less* hollow than today's `sm` — causing the exact
 * convergence a thicker stroke is meant to prevent. Verified in greyscale, which is the test that
 * matters: the shape is the non-colour carrier for WCAG 1.4.1, not the hue.
 *
 * **Where the cliff actually is:** past roughly `size-3` (12px) a 1px stroke drops under ~8% of the
 * diameter and the ring does start reading as a tinted disc. An altitude bigger than 8px must move to
 * `border-2`; nothing here is one, so nothing here does.
 *
 * `lg` is `size-2` rather than `size-2.5` so it stays the same instrument instead of becoming a louder
 * one: 0.5rem against `lg`'s 1.25rem mark is 40%, between `md`'s 37.5% and `sm`'s 50%.
 */
const STATUS_DOT_SIZE: Record<StripSize, string> = {
  sm: 'size-1.5',
  md: 'size-1.5',
  lg: 'size-2',
};

/**
 * ENABLED, not "active" — the word is the whole point.
 *
 * What this dot knows is that three switches are on, so delivery is permitted. It knows nothing about
 * whether anything has ever been delivered: a sync minted an hour ago and never triggered reads exactly
 * the same as one that ran ten minutes ago. Labelling that "Sync active" claims motion the row itself
 * contradicts one column to the right, where Last run says "No runs yet".
 *
 * Note what the fix is NOT: a third dot state for never-run. That question belongs to the run axis and
 * is already answered by the Last run cell and the detail page's never-run fork, and putting it here
 * would turn a switch indicator into a health indicator — the job `SyncStatusBadge` already holds. The
 * dot was overclaiming, so the dot says less; it does not learn a new state. See `ActivationStatus`.
 */
const STATUS_TITLE: Record<'enabled' | 'disabled', string> = {
  enabled: 'Sync enabled',
  disabled: 'Sync disabled',
};

/**
 * The pilot light: an always-present state mark at the head of the strip.
 *
 * It exists because the thing it replaced said nothing. Enabled/disabled used to live in a dedicated
 * `Status` column that rendered a red `Disabled` badge for a disabled sync and LITERALLY NOTHING for an
 * enabled one — "mark the exception, not the rule", whose case was that an `Active` badge announces the
 * default in the loudest column. That case is real and this trades it away knowingly: what it did not
 * survive was a column with its own header that is blank on every healthy row, which does not read as
 * "Active" — it reads as data that failed to load.
 *
 * Head placement, not a mark on the connector, and the original reason for it is now spent: the
 * connector used to wander (§ the 193.9px in {@link StripLayout}) so a mark on it could never be scanned
 * as a column, and the replacement for a column had to read as one. Under `tracks` both positions are
 * columns, so scannability is a tie. What still decides it is reading order — the head dot is the first
 * ink on the row, a connector mark is read third, after a name that can run forty-one characters, and
 * the question this list exists to answer is "which of my syncs is off". Plus one channel, one job: the
 * arrow track is now the only thing separating the two names, and a column of red arrows through the
 * middle of the table would be a loud stripe on exactly the rows meant to be the quiet exception.
 *
 * Rendered INSIDE the near endpoint rather than as a fourth grid track, which costs nothing and buys
 * two things: it still starts at the strip's left edge on every row, so it is still a perfect column;
 * and a dotless caller (the wizard) keeps the very same template with the arrow still aligned.
 */
function ActivationStatusDot({ enabled, size }: { enabled: boolean; size: StripSize }): React.ReactElement {
  const state = enabled ? 'enabled' : 'disabled';
  // `aria-hidden` with no sibling `sr-only`: the state is already in the strip's connective
  // ({@link CONNECTIVE}), and saying it twice in one sentence is worse than saying it once. The `title`
  // is for the mouse, which the connective does not serve, and is not announced on a hidden node.
  return (
    <span
      aria-hidden
      title={STATUS_TITLE[state]}
      data-slot="activation-status-dot"
      data-state={state}
      className={cn(STATUS_DOT_SIZE[size], 'shrink-0 rounded-full', STATUS_DOT_CLASSES[state])}
    />
  );
}

interface ActivationEndpointProps {
  /** Which half of the relation this is. Picks the glyph AND the screen-reader role word. */
  kind: 'audience' | 'destination';
  label: string;
  /** `destination` only — the registry key for the brand mark, e.g. `FB_CUSTOM_AUDIENCE`. */
  definitionName?: string | null | undefined;
  /** `audience` only — the source's warehouse type. Empty/absent keeps the `Users` fallback. */
  warehouseType?: string | undefined;
  size?: StripSize;
  /** The label is an id or a fallback rather than a name — typeset it as one. */
  isIdentifier?: boolean;
  /** `false` inside a row-activator `<button>`: a tooltip trigger there is a second tab stop. */
  tooltips?: boolean;
  /**
   * Renders the LABEL as a router link. Opt-in for the same reason `tooltips` is: this strip is drawn
   * inside a row-activator `<button>` on the table, and an `<a>` nested in a `<button>` is invalid
   * markup and an unreachable control. Only a caller that knows it is NOT inside an activator passes
   * it — today that is the detail header alone, where the strip is the `<h1>`.
   *
   * Pass an already-`wp()`-prefixed path. Absent leaves the label exactly as it was, which is what
   * every caller without a resolved id must do — see {@link audiencePath}.
   *
   * It takes precedence over `tooltips`: the full string is recovered through `title` instead, because
   * a `TruncateWithTooltip` trigger wrapping a link is two nested interactive elements where the strip
   * wants one.
   */
  href?: string | undefined;
  /**
   * Rendered before the glyph, inside this endpoint's own box. One caller and one node: the strip puts
   * {@link ActivationStatusDot} here on the near side. A slot rather than an `enabled` prop of its own:
   * an endpoint is half a relation and has no sync state of its own to answer with, and widening its
   * API with a state it cannot always supply is how a half-strip ends up asserting one. (The concrete
   * caller that forced this was `TwoSidedDelta`, which composed the two endpoints across a divider;
   * it was retired with the run-detail redesign, but the argument does not depend on it.)
   */
  leading?: React.ReactNode;
  className?: string;
}

/**
 * One end of the strip.
 *
 * The export currently has no consumer outside this module and its test. It was added for
 * `TwoSidedDelta`, which split the two sides across a divider with the run's numbers *between* them —
 * the split WAS the instrument, so composing a single-line strip there would have drawn the relation
 * twice on one card. That component was retired when the run detail became a phase stack; the phase
 * stack states the pairing through its marks and never needs half a strip. Kept exported rather than
 * narrowed to a module-private helper because the next surface that needs one end of the pairing must
 * compose this rather than hand-roll a third copy — which is the whole reason it exists.
 */
export function ActivationEndpoint({
  kind,
  label,
  definitionName,
  warehouseType,
  size = 'sm',
  isIdentifier = false,
  tooltips = true,
  leading,
  href,
  className,
}: ActivationEndpointProps): React.ReactElement {
  const warehouseLabel = kind === 'audience' ? warehouseTypeLabel({ warehouseType }) : null;
  const Mark =
    kind === 'audience'
      ? warehouseLabel !== null && isKnownWarehouseType(warehouseType ?? '')
        ? warehouseTypeMeta(warehouseType ?? '').Icon
        : Users
      : destinationTypeIcon(definitionName).Icon;
  const labelClasses = cn(LABEL_CLASSES[size], isIdentifier && IDENTIFIER_LABEL_CLASSES[size]);

  return (
    <span className={cn('flex min-w-0 items-center', FRAME_CLASSES[size], className)}>
      {leading}
      {/* Decorative by construction — the brand marks are `<img alt="">`s and the lucide glyphs carry
          no label, so the wrapper hides the whole slot rather than each mark hiding itself. The
          `data-slot` is the only stable handle on a mark that has, deliberately, no accessible one. */}
      <span aria-hidden data-slot={`activation-${kind}-mark`} className="flex shrink-0 items-center">
        <Mark className={MARK_CLASSES[size]} />
      </span>
      <span className="sr-only">{ENDPOINT_ROLE[kind]}</span>
      {href !== undefined ? (
        // The link's accessible name is the bare label, and it must STAY that way: an `aria-label`
        // here (`View audience X`) would replace this node's subtree in the heading's name
        // computation, so the detail page's `<h1>` would read "Audience View audience X activates
        // into …". The role word is already carried by the `sr-only` span outside the link.
        //
        // Underline on hover AND on focus-visible — the focus one is the visible focus indicator, and
        // it is stated in shape rather than colour so it survives the theme flip with no token.
        <Link
          to={href}
          title={label}
          className={cn('min-w-0 truncate underline-offset-4 hover:underline focus-visible:underline', labelClasses)}
        >
          {label}
        </Link>
      ) : tooltips ? (
        <TruncateWithTooltip className={labelClasses}>{label}</TruncateWithTooltip>
      ) : (
        // `title` rather than a tooltip: same recovery of the full string, no extra focusable.
        <span title={label} className={cn('min-w-0 truncate', labelClasses)}>
          {label}
        </span>
      )}
    </span>
  );
}

interface ActivationStripProps {
  /** The Lookout audience — resolve it with `audienceLabel()`; a broken reference stays its raw id. */
  audienceLabel: string;
  /** The far side — resolve it with `destinationAudienceLabel()` and its three-rung ladder. */
  destinationLabel: string;
  /** Registry key for the far side's brand mark, e.g. `FB_CUSTOM_AUDIENCE`. Absent falls back. */
  destinationDefinitionName?: string | null | undefined;
  /** The near side's warehouse type. Empty/absent keeps the `Users` fallback. */
  warehouseType?: string | undefined;
  /** `!hasResolvedDestinationName(row)` — the far-side label is an id or an account, not a name. */
  destinationIsIdentifier?: boolean;
  size?: StripSize;
  /**
   * Whether the sync is switched on — `row.enabled` / `detail.sync.enabled`. Drives the pilot light and
   * the tense of the strip's `sr-only` connective.
   *
   * OMITTED means "there is no sync yet", and no dot is drawn. That is the Activate wizard, which draws
   * this strip for a sync nobody has pressed the button on: a preview asserting a live sync is the one
   * thing this product refuses to do. Making it the absence of a prop rather than a `showDot` flag is
   * deliberate — a flag is something the next caller can get wrong in both directions, whereas here the
   * only way to draw a dot is to hand over the actual state, and the only way to draw a GREEN one is to
   * hand over `true`. Note what this is NOT: a default. An absent `enabled` does not mean `true`, so a
   * caller who forgets it renders a sync with no claim about its state, never a disabled one as live.
   */
  enabled?: boolean | undefined;
  /** `flow` (default) or the aligned three-track template — see {@link StripLayout}. */
  layout?: StripLayout;
  /** `false` inside a row-activator `<button>`. See the header comment. */
  tooltips?: boolean;
  /**
   * Workspace-prefixed path to the Lookout audience — makes the NEAR side a link, and only the near
   * side. Build it with `audiencePath()` + `wp()`, and pass it only from a caller that is not inside a
   * row-activator `<button>`; see {@link ActivationEndpointProps.href}.
   *
   * There is deliberately no far-side twin. The far side is Meta's or TikTok's audience, which lives
   * outside this product and which we hold only an id and a snapshot of a name for — there is nowhere
   * in the app to send a reader, and a link off to an ad platform is not this strip's job.
   */
  audienceHref?: string | undefined;
  className?: string;
}

export function ActivationStrip({
  audienceLabel,
  destinationLabel,
  destinationDefinitionName,
  warehouseType,
  destinationIsIdentifier = false,
  size = 'sm',
  enabled,
  layout = 'flow',
  tooltips = true,
  audienceHref,
  className,
}: ActivationStripProps): React.ReactElement {
  return (
    <span className={cn(LAYOUT_CLASSES[layout], FRAME_CLASSES[size], className)}>
      {/* `flow` only. The near side is the ONLY one that yields, until the far side hits its cap.
          Under `tracks` this whole paragraph is replaced by the template — same doctrine (the far side
          is protected, the near side pays), enforced by a reservation instead of a cap.

          The two halves must not share the shortfall in proportion to their length, which is what
          plain flex shrinking does and what this looked like for one round: measured in a browser at
          1280×800, in a 429px cell, a 42-character audience name and the 7-character `chandu1` both
          truncated — `chandu1` rendered as `chan…` with thirty characters of the audience still on
          screen. Clipping a name that FITS is the one failure this strip cannot have, and weighting the
          shrink factors only moved the cliff a character or two: flexbox has no way to say "exhaust A
          before touching B".

          So it is said with a cap instead. The far side does not shrink at all (`shrink-0`) and is
          bounded at three fifths of the strip; the near side absorbs everything else. The far side gets
          the protection because it is the discriminator — it was this row's title before the strip
          existed, and it is the only field separating two syncs into the same ad account. The cap is
          what keeps that from becoming a licence to swallow the row: past three fifths the far side
          truncates too, and the audience always keeps two fifths to be recognised by. */}
      <ActivationEndpoint
        kind="audience"
        label={audienceLabel}
        warehouseType={warehouseType}
        size={size}
        tooltips={tooltips}
        href={audienceHref}
        leading={enabled === undefined ? undefined : <ActivationStatusDot enabled={enabled} size={size} />}
      />
      <ArrowRight aria-hidden className={MARK_CLASSES[size]} />
      {/* The arrow's meaning, since the arrow itself is decorative. `sr-only` is absolutely
          positioned, so it costs no gap and shifts nothing — and, under `tracks`, claims no track. The
          padding spaces are load-bearing: without them the two sides concatenate into `Lapsed
          buyersactivates into`. */}
      <span className="sr-only">{CONNECTIVE[enabled === false ? 'disabled' : 'enabled']}</span>
      <ActivationEndpoint
        kind="destination"
        label={destinationLabel}
        definitionName={destinationDefinitionName}
        size={size}
        isIdentifier={destinationIsIdentifier}
        tooltips={tooltips}
        className={FAR_SIDE_CLASSES[layout]}
      />
    </span>
  );
}
